// Inline autocomplete from the Price Book for estimate/invoice line items.
// Type a few chars → see matching services with their prices → click to
// auto-fill the line's name + price. Saves owners from re-typing common items.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { G, styles, api } from '../theme.js';

export function PriceBookPicker({ value, onPick, placeholder = 'Description (type to search price book…)' }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    api('/api/pricebook').then(r => setItems(Array.isArray(r) ? r : (r?.items || []))).catch(()=>{});
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Filter + group by category (ServiceTitan-style). When the search box is
  // empty we show every category as a collapsible browse mode so the owner
  // can walk the catalog without typing. When typing, results filter inline
  // and the matching categories auto-expand.
  // Multi-word fuzzy match: "trip charge" matches "Diagnostic Trip Charge".
  // Hay includes SKU now too so techs can type "SKU-1234" → exact item.
  const { groups, totalMatched } = useMemo(() => {
    const term = String(value || '').trim().toLowerCase();
    const tokens = term.split(/\s+/).filter(Boolean);
    const matched = items.filter(it => {
      if (!term) return true;
      const hay = `${it.name||''} ${it.sku||''} ${it.category||''} ${it.description||''}`.toLowerCase();
      // Every token must appear (AND match) — beats substring for multi-word queries.
      return tokens.every(t => hay.includes(t));
    });
    matched.sort((a, b) => {
      if (term) {
        const aStart = String(a.name||'').toLowerCase().startsWith(term) ? 0 : 1;
        const bStart = String(b.name||'').toLowerCase().startsWith(term) ? 0 : 1;
        if (aStart !== bStart) return aStart - bStart;
      }
      return String(a.name||'').localeCompare(String(b.name||''));
    });
    const tree = {};
    for (const it of matched) {
      const parts = (it.category || 'Uncategorized').split('/').map(s => s.trim()).filter(Boolean);
      const top = parts[0] || 'Uncategorized';
      const sub = parts[1] || 'General';
      tree[top] = tree[top] || {};
      tree[top][sub] = tree[top][sub] || [];
      tree[top][sub].push(it);
    }
    // Show every match when searching (no cap) so nothing is hidden. Browse
    // mode caps at 500 — beyond that the dropdown gets too big and the user
    // should type a search term anyway. The "X of Y" header makes the cap
    // visible.
    const cap = term ? matched.length : 500;
    let count = 0;
    const capped = {};
    for (const top of Object.keys(tree).sort()) {
      capped[top] = {};
      for (const sub of Object.keys(tree[top]).sort()) {
        if (count >= cap) break;
        const slice = tree[top][sub].slice(0, Math.max(1, cap - count));
        capped[top][sub] = slice;
        count += slice.length;
      }
    }
    return { groups: capped, totalMatched: matched.length };
  }, [items, value]);

  return <div ref={wrapRef} style={{ position:'relative' }}>
    <input
      type="text"
      value={value || ''}
      onChange={e=>{ onPick({ name: e.target.value }); setOpen(true); }}
      onFocus={()=>setOpen(true)}
      placeholder={placeholder}
      style={{ ...styles.inp, width:'100%' }}
      autoComplete="off"
    />
    {open && <div style={{
      position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:200,
      ...styles.card, padding:6, maxHeight:480, overflowY:'auto',
      boxShadow:'0 8px 24px rgba(0,0,0,.3)',
    }}>
      <div style={{ fontSize:10, fontWeight:700, color:G.textDim, textTransform:'uppercase', padding:'4px 10px', display:'flex', justifyContent:'space-between' }}>
        <span>Price Book {value ? `· ${totalMatched} match${totalMatched===1?'':'es'}` : `· browse ${items.length} items`}</span>
        {items.length === 0 && <a href="#" onClick={(e)=>{ e.preventDefault(); window.dispatchEvent(new CustomEvent('tradeops:nav', { detail:{ tab:'pricebook' }})); }} style={{ color:G.cyan }}>Add items →</a>}
      </div>
      {items.length === 0 && <div style={{ padding:'12px 14px', fontSize:13, color:G.textMuted, textAlign:'center' }}>
        No price-book items yet. Click "Add items →" to seed your catalog so you can pick them here.
      </div>}
      {items.length > 0 && Object.keys(groups).length === 0 && <div style={{ padding:'12px 14px', fontSize:13, color:G.textMuted, textAlign:'center' }}>
        No matches for "{value}". Try fewer words — or use the SKU.
      </div>}
      {Object.keys(groups).map(top => {
        const subs = groups[top];
        const topCount = Object.values(subs).reduce((s, arr) => s + arr.length, 0);
        return <div key={top} style={{ marginTop:4 }}>
          <div style={{ fontSize:11, fontWeight:800, color:G.purple, padding:'6px 10px', textTransform:'uppercase', letterSpacing:'.05em', borderTop:`1px solid ${G.cardBorder}` }}>
            {top} <span style={{ color:G.textMuted, fontWeight:600 }}>({topCount})</span>
          </div>
          {Object.keys(subs).map(sub => <div key={sub}>
            {Object.keys(subs).length > 1 && <div style={{ fontSize:10, fontWeight:700, color:G.textMuted, padding:'2px 14px' }}>{sub}</div>}
            {subs[sub].map(it => (
              <div key={it.id} onClick={() => { onPick({ name: it.name, price: Number(it.price)||0, qty: 1, unit: it.unit || 'each', priceBookId: it.id }); setOpen(false); }}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 14px 8px 18px', borderRadius:6, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=G.input}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{it.name}</div>
                  {(it.description || it.unit) && <div style={{ fontSize:11, color:G.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {it.unit ? `per ${it.unit}` : ''}{it.unit && it.description ? ' · ' : ''}{it.description ? it.description.slice(0,60) : ''}
                  </div>}
                </div>
                <div style={{ fontWeight:700, color:G.green, fontSize:14, marginLeft:10, whiteSpace:'nowrap' }}>
                  ${Number(it.price||0).toFixed(2)}
                </div>
              </div>
            ))}
          </div>)}
        </div>;
      })}
    </div>}
  </div>;
}
